<template>
  <div
    class="relative h-full w-full min-h-0 overflow-hidden"
    :class="isImageRounded"
    @mouseenter="show"
    @mouseleave="hide"
  >
    <div class="relative flex h-full w-full min-h-0 items-center justify-center">
      <a
        v-show="!isLoading && !hasError"
        href="#"
        class="h-full w-full"
        @click.prevent.stop="onImgClick"
      >
        <img
          v-touch.stop="onImgClick"
          :src="path"
          :alt="file.filename"
          data-test="file-image"
          class="h-full w-full object-cover"
          @load="onImageLoad"
          @error="onImageError"
        >
      </a>
      <wui-icon
        v-show="isLoading && !hasError"
        name="spinner"
        class="self-center"
      />
      <wui-icon
        v-show="hasError"
        name="warningM"
        class="self-center text-fg-base-fg22"
        data-test="file-image-error"
      />
    </div>

    <div
      v-show="hover"
      class="
        absolute left-0 top-0 h-full w-full cursor-pointer
        bg-linear-to-b from-black to-transparent opacity-25
      "
      @click.stop="onImgClick"
    />

    <div
      v-show="hover"
      class="absolute right-3 top-3"
    >
      <wui-btn
        icon
        variant="neut"
        state="alpha"
        prepend-icon="save"
        :size="32"
        :title="file.filename"
        data-test="file-link"
        @click.stop="onSaveClick"
        @contextmenu.stop
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiIcon, WuiBtn } from '@wui/common-library'
import { IMFile } from '@wui/im'
import { computed, ref } from 'vue'

import { useHover } from '@/shared/composables/use-hover'
import { vTouch } from '@/shared/directives/v-touch'
import { download } from '@/shared/utils/download-files'
import {
  isChatImagePreviewCached,
  markChatImagePreviewCached,
} from '@/shared/utils/files'
import { getAuthenticatedMediaUrl } from '@/shared/utils/url'

const props = defineProps<{
  file: IMFile,
  isRounded?: boolean,
}>()

const emits = defineEmits(['img-click', 'loaded'])

const { hover, show, hide } = useHover()
const isLoading = ref(!isChatImagePreviewCached(props.file.filecookie))
const hasError = ref(false)

const onImageLoad = () => {
  markChatImagePreviewCached(props.file.filecookie)
  isLoading.value = false
  hasError.value = false
  emits('loaded')
}

const onImageError = () => {
  isLoading.value = false
  hasError.value = true
}

const onImgClick = () => {
  if (hasError.value) return
  emits('img-click', props.file)
}

const onSaveClick = () => {
  download(props.file, '/files/')
}

const path = computed(() => {
  return getAuthenticatedMediaUrl(`/files/${props.file.filecookie}`)
})

const isImageRounded = computed(() => {
  return props.isRounded ? 'rounded-12' : ''
})
</script>
